import React from "react";
import {useMutation} from "react-query";
import {Formik, Form} from "formik";
import Alert from "react-s-alert";
import {mapValues} from "lodash";
import {DateFieldInput, TextField} from "@reside/ui";
import {validator} from "@reside/forms";
import {VerificationAnswersDto} from "@reside/reside-api-admission";
import {AdmissionEntryService} from "../../../../services/AdmissionEntryService";
import {buildResponseErrorMessage} from "../../../../services/api/errorHelpers";
import {SubmitButton} from "../../../page-admission-welcome/shared/admission-styles";
import {
  RESIDENT_FIRST_NAME,
  RESIDENT_LAST_NAME,
  RESIDENT_DATE_OF_BIRTH,
} from "../../../../models/AdmissionModel.helpers";
import {useFetchActiveThemeWithAdmissionToken} from "../../../page-admin-organizations-branding/hooks/useFetchActiveThemeWithAdmissionToken";
import {Fieldset} from "../../styles";
import {dispatch} from "../../../../store";

export const VerifyAccessFormRenderer = ({
  isLoading,
  onSubmit,
}: {
  isLoading: boolean;
  onSubmit: (data: VerificationAnswersDto) => void;
}) => (
  <Formik<any>
    initialValues={{
      [RESIDENT_LAST_NAME]: "",
      [RESIDENT_FIRST_NAME]: "",
      [RESIDENT_DATE_OF_BIRTH]: "",
    }}
    validate={values =>
      validator.validateAll(values, {
        [RESIDENT_LAST_NAME]: "required",
        [RESIDENT_FIRST_NAME]: "required",
        [RESIDENT_DATE_OF_BIRTH]: ["required", "date", "date_in_past"],
      })
    }
    onSubmit={(data: VerificationAnswersDto) =>
      onSubmit(mapValues(data, value => value.trim()))
    }
  >
    {({submitForm}) => (
      <Form autoComplete="off">
        <Fieldset>
          <TextField name={RESIDENT_FIRST_NAME} label="Resident First Name" />
          <TextField name={RESIDENT_LAST_NAME} label="Resident Last Name" />
          <DateFieldInput
            name={RESIDENT_DATE_OF_BIRTH}
            label="Resident Date of Birth"
          />
        </Fieldset>
        <SubmitButton
          size="large"
          color="primary"
          disabled={isLoading}
          onClick={submitForm}
        >
          {isLoading ? "Processing..." : "Continue with Admission"}
        </SubmitButton>
      </Form>
    )}
  </Formik>
);

export const VerifyAnswersForm = ({
  onVerified,
  onUnauthorized,
}: {
  onVerified: (token: string) => void;
  onUnauthorized: () => void;
}) => {
  const {mutate: fetchActiveTheme} = useFetchActiveThemeWithAdmissionToken();

  const {mutate, isLoading} = useMutation(
    AdmissionEntryService.enterAdmission,
    {
      onError: async (response: any) => {
        if (response?.status === 401) {
          Alert.error(
            "Your link has expired. Please request a new login link.",
          );
          onUnauthorized();
          return;
        } else {
          Alert.error(await buildResponseErrorMessage(response));
        }
      },
      onSuccess: ({data: {token}}) => {
        const isThemeFetched = localStorage.getItem("theme-fetched") === "true"; // Theme will not be fetched when RE is started from email

        dispatch.adminSession.logout({resetAdmissions: false}); // Prevent admissions data reset during admin → RE transition

        // Ensure all API calls are completed before refreshing the page to prevent network cancellation errors.
        // This is a temporary solution until we implement proper theme fetching via admission email entry token in ticket DEV-3171
        // by adding the admission ID to it, which will enable us to determine which theme to fetch.
        if (!isThemeFetched) {
          try {
            fetchActiveTheme(
              {token},
              {
                onSuccess: () => {
                  onVerified(token);
                },
                onError: () => {
                  // Even if theme fetching fails, we still want to proceed with admission
                  onVerified(token);
                },
              },
            );
          } catch (error) {
            // If there's an error with the theme fetching, still proceed with admission
            onVerified(token);
          }
        } else {
          // If the theme is already fetched, directly proceed with admission
          onVerified(token);
        }
      },
    },
  );

  return <VerifyAccessFormRenderer isLoading={isLoading} onSubmit={mutate} />;
};
